import {FC, useState, useEffect} from 'react'
import {Dispatch} from 'redux'
import {Button, Modal} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {
  getFreeAndPaidSubscriptionPlan,
  handleselectedSubscriptionPlan,
} from '../../../../setup/redux/subscription_plans/actions'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import { components } from 'react-select';
import {customSelectHoverStyles} from '../../../services/Styles'
import CustomSelect from '../../../components/common/Select'

type Props = {
  row: any
  isSubscriptionPlanDialog: boolean
  isEditPlan: boolean
  onClose: () => void
  onClickSubmit: () => void
  onSubscriptionTypeChange?: (type: string) => void
}

interface Plan {
  value: string
  label: string
  monthlyPrice?: number
  yearlyPrice?: number
}

interface SubscriptionType {
  value: string
  label: string
}

const SubscriptionPlanDialog: FC<Props> = ({
  isSubscriptionPlanDialog,
  onClose,
  onClickSubmit,
  isEditPlan,
  row,
  onSubscriptionTypeChange,
}) => {
  const {freeAndPaidPlanList}: any = useSelector<RootState>(
    (state) => ({
      freeAndPaidPlanList: state.subscriptionPlans.freeAndPaidPlanList,
    }),
    shallowEqual
  )
  const navigate = useNavigate()
  const dispatch: Dispatch<any> = useDispatch()
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined)
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<string>('monthly')
  const [planList, setPlanList] = useState<Plan[]>([])
  const [subscriptionTypeList] = useState<SubscriptionType[]>([
    {value: 'monthly', label: 'Monthly'},
    {value: 'yearly', label: 'Yearly'},
  ])
  const selectedPlanType =
    row?.plans && row.plans.length > 0 ? row?.plans[0]?.subscription?.type : undefined
  
  const selectedSubscriptionTypeFromData =
    row?.plans && row.plans.length > 0 ? row?.plans[0]?.subscription_type : undefined


  const isEnterpriseUpgrade = row?.request_type === 'enterprise_upgrade'
  const upgradeSubscriptionIdFromData = isEnterpriseUpgrade ? row?.upgrade_subscription_id : undefined
  const upgradeTypeFromData = isEnterpriseUpgrade ? row?.upgrade_type : undefined

  useEffect(() => {
    dispatch(getFreeAndPaidSubscriptionPlan())
  }, [dispatch])

  useEffect(() => {
    if (freeAndPaidPlanList.length > 0) {
      const options = freeAndPaidPlanList.map((plan: any) => ({
        value: plan.id.toString(),
        label: plan.name,
        monthlyPrice: plan.monthly_price,
        yearlyPrice: plan.yearly_price,
        className: '',
      }))
      setPlanList(options)
    }
  }, [freeAndPaidPlanList])

  useEffect(() => {
    if (selectedPlanType !== 'enterprise') {
      if (planList.length > 0) {
        const planIdToFind = isEnterpriseUpgrade 
          ? upgradeSubscriptionIdFromData?.toString() 
          : (row?.plans && row.plans.length > 0 ? row?.plans[0]?.subscription?.id?.toString() : '')
        
        const selectedPlanObj = planList.find(
          (n: Plan) => n.value === planIdToFind
        )
        if (selectedPlanObj !== undefined) {
          setSelectedPlan(selectedPlanObj.value)
          dispatch(handleselectedSubscriptionPlan(selectedPlanObj.value))
        }
      }
    }
  }, [selectedPlanType, planList, isEnterpriseUpgrade, upgradeSubscriptionIdFromData])

  useEffect(() => {
    if (selectedSubscriptionTypeFromData) {
      setSelectedSubscriptionType(selectedSubscriptionTypeFromData)
    } else if (isEnterpriseUpgrade && upgradeTypeFromData) {
      setSelectedSubscriptionType(upgradeTypeFromData)
    }
  }, [selectedSubscriptionTypeFromData, isEnterpriseUpgrade, upgradeTypeFromData])

  const handleChange = (option: any) => {
    const selectedValue = option?.value
    setSelectedPlan(selectedValue)
    dispatch(handleselectedSubscriptionPlan(selectedValue))
    if (selectedValue === 'addNewPlan') {
      navigate('/plans/plans-management/create')
      dispatch(handleselectedSubscriptionPlan(null))
    }
  }

  const handleSubscriptionTypeChange = (option: any) => {
    setSelectedSubscriptionType(option?.value)
    if (onSubscriptionTypeChange) {
      onSubscriptionTypeChange(option?.value)
    }
  }

  const Option = (props: any) => {
    const {label, className, data, ...rest} = props
    return (
      <components.Option {...rest}>
        <div className='d-flex flex-column'>
          <span className={data.className}>{label}</span>
          <small className='text-muted'>
            Monthly: €{data.monthlyPrice || 0} | Yearly: €{data.yearlyPrice || 0}
          </small>
        </div>
      </components.Option>
    )
  }

  return (
    <>
      <Modal show={isSubscriptionPlanDialog} onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditPlan ? 'Edit' : 'Select'} Subscription Plan </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='col-md-12'>
            <div className='form-group mb-5 position-relative'>
              <label className='form-label' htmlFor='selectPlan'>
                Select Plan
              </label>
              <CustomSelect
                options={planList}
                name='subscription_plan'
                menuPosition={'fixed'}
                onChange={handleChange}
                components={{Option}}
                value={
                  selectedPlan
                    ? {
                        ...planList.find((option) => option.value === selectedPlan),
                        label:
                          planList
                            .find((option) => option.value === selectedPlan)
                            ?.label?.split(' - ')[0] || '',
                      }
                    : null
                }
                classNamePrefix='react-custom-select'
                styles={customSelectHoverStyles}
              />
            </div>
            <div className='form-group mb-5 position-relative'>
              <label className='form-label' htmlFor='selectSubscriptionType'>
                Subscription Type
              </label>
              <CustomSelect
                options={subscriptionTypeList}
                name='subscription_type'
                menuPosition={'fixed'}
                onChange={handleSubscriptionTypeChange}
                value={subscriptionTypeList.find(
                  (option) => option.value === selectedSubscriptionType
                )}
                classNamePrefix='react-custom-select'
                styles={customSelectHoverStyles}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClose}>
            Close
          </Button>
          <Button variant='primary' onClick={onClickSubmit} disabled={!selectedPlan || !selectedSubscriptionType}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {SubscriptionPlanDialog}
